import {
  DEFAULT_PALM_STYLUS_SEPARATION_PX,
  getPointerType,
  sanitizeInkSettings
} from "./inkDeviceProfile";

function finiteNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function snapshotInkPointer(event) {
  const pointerId = Number(event?.pointerId);
  const clientX = Number(event?.clientX);
  const clientY = Number(event?.clientY);
  if (!Number.isFinite(pointerId) || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
    return null;
  }
  return {
    pointerId,
    pointerType: getPointerType(event),
    clientX,
    clientY,
    width: Math.max(0, finiteNumber(event?.width)),
    height: Math.max(0, finiteNumber(event?.height)),
    pressure: finiteNumber(event?.pressure),
    tiltX: finiteNumber(event?.tiltX),
    tiltY: finiteNumber(event?.tiltY),
    twist: finiteNumber(event?.twist),
    tangentialPressure: finiteNumber(event?.tangentialPressure),
    isPrimary: event?.isPrimary !== false,
    timeStamp: finiteNumber(
      event?.timeStamp,
      typeof performance !== "undefined" ? performance.now() : Date.now()
    )
  };
}

function ignoredAction(pointerId = null, reason = "not-owner") {
  return {
    type: "ignore",
    pointerId,
    reason,
    samples: [],
    handSeparationPx: 0,
    browserLimited: false
  };
}

const PALM_MEMORY_MS = 6000;
const MAX_PALM_MEMORIES = 8;

// Ink starts on the first contact without delay. If a touch-only browser then
// reports a clearly separated upper contact, ownership transfers to that stylus
// and the provisional lower palm stroke is discarded by the caller.
export function createInkPointerOwnership(initialSettings = {}) {
  let settings = sanitizeInkSettings(initialSettings);
  let contacts = new Map();
  let ownerId = null;
  let palmMemories = [];

  function updateContact(sample, options = {}) {
    const previous = contacts.get(sample.pointerId);
    const contact = {
      ...previous,
      pointerId: sample.pointerId,
      pointerType: sample.pointerType,
      clientX: sample.clientX,
      clientY: sample.clientY,
      minY: Math.min(previous?.minY ?? sample.clientY, sample.clientY),
      maxY: Math.max(previous?.maxY ?? sample.clientY, sample.clientY),
      width: sample.width,
      height: sample.height,
      startedAt: previous?.startedAt ?? sample.timeStamp,
      lastAt: sample.timeStamp,
      blocked: options.blocked === undefined ? Boolean(previous?.blocked) : Boolean(options.blocked),
      samples: [...(previous?.samples || []), sample].slice(-96)
    };
    contacts.set(sample.pointerId, contact);
    return contact;
  }

  function getRequiredHandSeparation() {
    return Number(settings.palmStylusSeparationPx || DEFAULT_PALM_STYLUS_SEPARATION_PX);
  }

  function getRequiredWritingClearance() {
    return Math.max(16, Math.min(48, getRequiredHandSeparation() * 0.2));
  }

  function getRequiredPairSeparation() {
    return settings.palmCalibrationReady
      ? getRequiredWritingClearance()
      : getRequiredHandSeparation();
  }

  function getBlockedPalmContacts(exceptPointerId = null) {
    return [...contacts.values()].filter((contact) => (
      contact.blocked
      && contact.pointerId !== exceptPointerId
    ));
  }

  function prunePalmMemories(atTime) {
    const now = finiteNumber(atTime);
    palmMemories = palmMemories.filter((memory) => memory.expiresAt >= now);
  }

  function rememberPalmContact(contact) {
    if (!contact) return;
    const atTime = finiteNumber(contact.lastAt, contact.startedAt);
    prunePalmMemories(atTime);
    const memory = {
      topY: contact.clientY - Math.max(0, contact.height) / 2,
      bottomY: contact.clientY + Math.max(0, contact.height) / 2,
      clientX: contact.clientX,
      width: contact.width,
      rememberedAt: atTime,
      expiresAt: atTime + PALM_MEMORY_MS
    };
    const duplicateIndex = palmMemories.findIndex((candidate) => (
      Math.abs(candidate.topY - memory.topY) < 160
      && Math.abs(candidate.clientX - memory.clientX) < 220
    ));
    if (duplicateIndex >= 0) palmMemories.splice(duplicateIndex, 1);
    palmMemories.push(memory);
    palmMemories = palmMemories.slice(-MAX_PALM_MEMORIES);
  }

  function getKnownPalmContacts(exceptPointerId, atTime) {
    prunePalmMemories(atTime);
    return [
      ...getBlockedPalmContacts(exceptPointerId),
      ...palmMemories.map((memory) => ({
        clientY: (memory.topY + memory.bottomY) / 2,
        height: memory.bottomY - memory.topY
      }))
    ];
  }

  function getHandSeparation(contact) {
    if (!contact) return 0;
    const palmContacts = getKnownPalmContacts(contact.pointerId, contact.lastAt);
    if (!palmContacts.length) return 0;
    const topmostPalmY = Math.min(...palmContacts.map((candidate) => (
      candidate.clientY - Math.max(0, candidate.height) / 2
    )));
    const separation = topmostPalmY - contact.clientY;
    return Number.isFinite(separation) && separation > 0 ? separation : 0;
  }

  function shouldArbitrateTouchStylus(sample) {
    return sample.pointerType === "touch"
      && ["active-stylus", "capacitive-stylus"].includes(settings.writingTool);
  }

  function getPairSeparation(upper, lower) {
    if (!upper || !lower) return 0;
    const lowerTop = lower.clientY - Math.max(0, lower.height) / 2;
    const separation = lowerTop - upper.clientY;
    return Number.isFinite(separation) && separation > 0 ? separation : 0;
  }

  function claim(contact, options = {}) {
    ownerId = contact.pointerId;
    contact.blocked = false;
    contacts.forEach((candidate, pointerId) => {
      if (pointerId !== ownerId) candidate.blocked = true;
    });
    return {
      type: "start",
      pointerId: ownerId,
      pointerType: contact.pointerType,
      samples: contact.samples.slice(),
      handSeparationPx: getHandSeparation(contact),
      requiredHandSeparationPx: getRequiredWritingClearance(),
      palmDetectionSeparationPx: getRequiredHandSeparation(),
      blockedContactCount: Number(options.blockedContactCount || 0),
      palmReportedFirst: options.palmReportedFirst === true,
      replacedPointerId: Number.isFinite(Number(options.replacedPointerId))
        ? Number(options.replacedPointerId)
        : null,
      browserLimited: false
    };
  }

  function pointerDown(event) {
    const sample = snapshotInkPointer(event);
    if (!sample) return ignoredAction(null, "invalid-pointer");

    if (ownerId !== null) {
      const currentOwner = contacts.get(ownerId);
      const secondaryContact = updateContact(sample, { blocked: false });
      const canCompareTouchPair = currentOwner
        && shouldArbitrateTouchStylus(currentOwner)
        && shouldArbitrateTouchStylus(secondaryContact);
      if (canCompareTouchPair) {
        const ownerTop = currentOwner.clientY - Math.max(0, currentOwner.height) / 2;
        const secondaryTop = secondaryContact.clientY - Math.max(0, secondaryContact.height) / 2;
        const upper = secondaryTop < ownerTop ? secondaryContact : currentOwner;
        const lower = upper.pointerId === secondaryContact.pointerId ? currentOwner : secondaryContact;
        if (getPairSeparation(upper, lower) >= getRequiredPairSeparation()) {
          lower.blocked = true;
          rememberPalmContact(lower);
          if (upper.pointerId !== ownerId) {
            const replacedPointerId = ownerId;
            return claim(upper, {
              blockedContactCount: 1,
              palmReportedFirst: true,
              replacedPointerId
            });
          }
          return ignoredAction(sample.pointerId, "secondary-contact");
        }
      }
      secondaryContact.blocked = true;
      rememberPalmContact(secondaryContact);
      return ignoredAction(sample.pointerId, "secondary-contact");
    }

    const retainedPalms = getKnownPalmContacts(null, sample.timeStamp);
    const contact = updateContact(sample, { blocked: false });

    // A real browser pen signal is authoritative. Touch-reported styli use the
    // calibrated vertical separation because Android often reports them as touch.
    if (sample.pointerType === "pen" || sample.pointerType === "mouse") {
      return claim(contact);
    }
    if (!retainedPalms.length) {
      return claim(contact);
    }
    if (getHandSeparation(contact) >= getRequiredWritingClearance()) {
      return claim(contact);
    }

    contact.blocked = true;
    rememberPalmContact(contact);
    return ignoredAction(sample.pointerId, "inside-palm-spacing");
  }

  function pointerMove(event) {
    const sample = snapshotInkPointer(event);
    if (!sample) return ignoredAction(null, "invalid-pointer");
    const existing = contacts.get(sample.pointerId);
    if (!existing) return ignoredAction(sample.pointerId, "unknown-pointer");
    const contact = updateContact(sample);
    if (ownerId !== sample.pointerId) {
      if (contact.blocked) rememberPalmContact(contact);
      return ignoredAction(sample.pointerId, contact.blocked ? "blocked-contact" : "not-owner");
    }
    return {
      type: "move",
      pointerId: ownerId,
      pointerType: contact.pointerType,
      samples: [sample],
      handSeparationPx: getHandSeparation(contact),
      requiredHandSeparationPx: getRequiredWritingClearance(),
      palmDetectionSeparationPx: getRequiredHandSeparation(),
      browserLimited: false
    };
  }

  function pointerUp(event, options = {}) {
    const sample = snapshotInkPointer(event);
    if (!sample) return ignoredAction(null, "invalid-pointer");
    const existing = contacts.get(sample.pointerId);
    if (!existing) return ignoredAction(sample.pointerId, "unknown-pointer");
    const contact = updateContact(sample);
    const wasOwner = ownerId === sample.pointerId;

    if (!wasOwner) {
      if (contact.blocked) rememberPalmContact(contact);
      contacts.delete(sample.pointerId);
      return ignoredAction(sample.pointerId, "blocked-contact-ended");
    }

    const cancelled = options.cancelled === true;
    const action = {
      type: cancelled ? "cancel" : "end",
      pointerId: sample.pointerId,
      pointerType: contact.pointerType,
      samples: cancelled ? [] : [sample],
      handSeparationPx: getHandSeparation(contact),
      requiredHandSeparationPx: getRequiredWritingClearance(),
      palmDetectionSeparationPx: getRequiredHandSeparation(),
      browserLimited: cancelled,
      reason: cancelled ? "owner-pointer-cancelled" : "owner-ended"
    };

    ownerId = null;
    contacts.delete(sample.pointerId);
    // Contacts that outlive the writing pointer are palms until they lift. This
    // prevents an already-resting hand from becoming the next stroke owner.
    contacts.forEach((remaining) => {
      remaining.blocked = true;
      rememberPalmContact(remaining);
    });
    return action;
  }

  return {
    pointerDown,
    pointerMove,
    pointerUp,
    setSettings(nextSettings) {
      settings = sanitizeInkSettings(nextSettings);
    },
    reset() {
      contacts = new Map();
      ownerId = null;
      palmMemories = [];
    },
    getOwnerId() {
      return ownerId;
    },
    getState() {
      return {
        ownerId,
        activeContactCount: contacts.size,
        blockedPalmCount: getBlockedPalmContacts().length,
        rememberedPalmCount: palmMemories.length,
        requiredHandSeparationPx: getRequiredWritingClearance(),
        palmDetectionSeparationPx: getRequiredHandSeparation()
      };
    }
  };
}
